import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../hooks/toast';
import { IHealthCertificate } from '../../shared/interfaces';
import api from '../../services/api';
import { MenuProps, Tag } from 'antd';
import {
  ATable,
  DropdownOptions,
  MenuOptions,
  PageIntro,
  PageLayout,
  PagePanel,
  StatusActive,
} from '../../components';
import {
  EditOutlined,
  FileAddOutlined,
  PlusCircleOutlined,
  PrinterOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { formatDate } from '../../shared/utils/format-date';
import { useAuth } from '../../hooks/auth';
import { PermissionActions } from '../../shared/enums/permissions.enum';

const HealthCertificates: React.FC = () => {
  const { client_id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { permissionFind } = useAuth();
  const [search, setSearch] = useState<string>('');
  const [healthCertificates, setHealthCertificates] = useState<
    IHealthCertificate[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState<number>();

  const listClientHealthCertificates = useCallback(async () => {
    setLoading(true);
    setHealthCertificates([]);
    try {
      const response = await api.post('/clients-health-certificates/list', {
        client_id: client_id,
        take: limit,
        skip: (page - 1) * limit,
        keyword: search,
      });
      setPages(response.data.count);
      setHealthCertificates(response.data.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro',
        description: 'Erro ao listar atestados',
      });
    } finally {
      setLoading(false);
    }
  }, [limit, search, page, addToast]);

  const renderMenuOptions = () => {
    const options: MenuProps['items'] = [];
    options.push({
      label: 'Atualizar',
      key: '1',
      icon: <RedoOutlined />,
      onClick: () => listClientHealthCertificates(),
    });
    if (!client_id) {
      if (permissionFind([PermissionActions.healthCertificatesCreate])) {
        options.push({
          label: 'Cadastrar',
          key: '2',
          icon: <PlusCircleOutlined />,
          onClick: () => navigate('/health_certificate/new'),
        });
      }
    }
    return options;
  };

  useEffect(() => {
    listClientHealthCertificates();
  }, [page, search, limit, listClientHealthCertificates]);

  return (
    <PageLayout>
      <PageIntro
        badge="Documentos"
        icon={<FileAddOutlined />}
        title="Atestados"
        description="Gerencie emissão, assinatura e impressão de atestados em uma listagem alinhada com o padrão visual do sistema."
      />
      <MenuOptions
        menu_items={renderMenuOptions()}
        showButtonBack={!!client_id}
        showInputSearch
        search={search}
        setSearch={setSearch}
      />
      <PagePanel>
        <ATable
          scroll={{ y: 510 }}
          columns={[
            {
              title: 'Id',
              width: 100,
              dataIndex: 'code',
            },
            {
              title: 'Data',
              dataIndex: 'date',
              width: 115,
              render: (value: string) => <>{formatDate(value)}</>,
            },
            {
              title: 'Cliente',
              dataIndex: 'client_name',
            },
            {
              title: 'Profissional',
              dataIndex: 'professional_name',
            },
            {
              title: 'Titulo',
              dataIndex: 'title_health_certificate',
            },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 95,
              render: (value: boolean) => <StatusActive status={value} />,
            },
            {
              title: 'Assinatura',
              width: 120,
              render: (cell: IHealthCertificate) => {
                const isSigned = Boolean(
                  cell.signature_validation_code || cell.signed_at,
                );

                return (
                  <Tag color={isSigned ? 'success' : 'warning'}>
                    {isSigned ? 'Assinado' : 'Não assinado'}
                  </Tag>
                );
              },
            },
            {
              title: 'Opções',
              width: 130,
              render: (cell: IHealthCertificate) => (
                <DropdownOptions
                  items={[
                    permissionFind([PermissionActions.healthCertificatesUpdate])
                      ? {
                          label: 'Editar',
                          key: '1',
                          icon: <EditOutlined />,
                          onClick: () => {
                            navigate(`/health_certificate/${cell.id}`);
                          },
                        }
                      : null,
                    {
                      label: 'Imprimir',
                      key: '2',
                      icon: <PrinterOutlined />,
                      onClick: () => {
                        navigate(`/health_certificate/${cell.id}/print`);
                      },
                    },
                  ]}
                />
              ),
            },
          ]}
          loading={loading}
          dataSource={healthCertificates}
          page={page}
          setPage={setPage}
          pages={pages}
          limit={limit}
          setLimit={setLimit}
        />
      </PagePanel>
    </PageLayout>
  );
};

export default HealthCertificates;
